import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { apiGetInvite, apiActivateInvite, apiResendInvite, isApiError } from '../lib/api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Login } from './Login'

const schema = z.object({
  name: z.string().optional(),
  password: z.string().min(8, 'Minimum 8 caractères'),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirmPassword'],
})

type FormValues = z.infer<typeof schema>

const resendFormSchema = z.object({ email: z.string().trim().email('Email invalide') })
type ResendFormValues = z.infer<typeof resendFormSchema>

type ViewState =
  | 'loading'
  | 'invalid'
  | 'invalid-resend-form'
  | 'invalid-resend-sent'
  | 'form'
  | 'activated'

export function InviteAccept({ token }: { token: string }) {
  const [state, setState] = useState<ViewState>('loading')
  const [inviteData, setInviteData] = useState<{ email: string; orgName: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [resendSubmitting, setResendSubmitting] = useState(false)

  const { control, handleSubmit, formState: { errors } } = useForm<FormValues>({
    mode: 'onBlur',
    resolver: zodResolver(schema),
    defaultValues: { name: '', password: '', confirmPassword: '' },
  })

  const resendForm = useForm<ResendFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(resendFormSchema),
    defaultValues: { email: '' },
  })

  useEffect(() => {
    ;(async () => {
      try {
        const data = await apiGetInvite(token)
        setInviteData(data)
        setState('form')
      } catch {
        setState('invalid')
      }
    })()
  }, [token])

  // Focus auto sur le champ email du mini-formulaire de resend pour Marie clavier-only.
  // On ne peut pas passer une ref à <Input> shadcn (pas forwardRef) → DOM API directe.
  useEffect(() => {
    if (state === 'invalid-resend-form') {
      document.getElementById('resend-email')?.focus()
    }
  }, [state])

  const onSubmit = async (data: FormValues) => {
    setSubmitting(true)
    try {
      await apiActivateInvite(token, { name: data.name || undefined, password: data.password })
      toast.success('Compte activé — Vous pouvez maintenant vous connecter', { duration: 6000 })
      // Évite délibérément window.location.reload() : (1) préserve le toast Sonner
      // (sinon avalé par le reload), (2) garantit l'atterrissage sur <Login /> et
      // pas sur le dashboard d'un autre user déjà loggué dans AuthContext,
      // (3) pas de re-bootstrap du bundle Vite.
      window.history.replaceState(null, '', '/')
      setState('activated')
    } catch {
      toast.error('Erreur lors de la création du compte', { duration: Infinity })
    } finally {
      setSubmitting(false)
    }
  }

  const onResend = resendForm.handleSubmit(async ({ email }) => {
    setResendSubmitting(true)
    try {
      await apiResendInvite(email.trim())
      setState('invalid-resend-sent')
    } catch (err) {
      if (isApiError(err) && err.status >= 500) {
        toast.error('Erreur serveur, réessayez plus tard')
      } else {
        // Même côté frontend, on ne distingue pas — bascule sur sent (anti-énumération).
        setState('invalid-resend-sent')
      }
    } finally {
      setResendSubmitting(false)
    }
  })

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-48 w-96 animate-pulse bg-gray-100 rounded-lg" aria-label="Vérification du lien d'invitation..." />
      </div>
    )
  }

  if (state === 'activated') {
    return <Login initialEmail={inviteData?.email} />
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-6 p-8 bg-white rounded-xl shadow text-center">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-red-600">Lien invalide</h2>
            <p className="text-sm text-muted-foreground">Ce lien d'invitation a expiré ou a déjà été utilisé.</p>
          </div>
          <div className="space-y-2">
            <Button
              type="button"
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => setState('invalid-resend-form')}
            >
              Demander un nouveau lien
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload() }}
            >
              Retour à la connexion
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (state === 'invalid-resend-form') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-6 p-8 bg-white rounded-xl shadow">
          <div>
            <h2 className="text-xl font-semibold">Recevoir un nouveau lien</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Renseignez l'email auquel l'invitation avait été envoyée. Nous vous renverrons un lien valable 7 jours.
            </p>
          </div>
          <form onSubmit={onResend} className="space-y-4">
            <Controller
              name="email"
              control={resendForm.control}
              render={({ field }) => (
                <div>
                  <Label htmlFor="resend-email">Email</Label>
                  <Input
                    id="resend-email"
                    type="email"
                    placeholder="vous@entreprise.com"
                    {...field}
                  />
                  {resendForm.formState.errors.email && (
                    <p className="text-sm text-red-600 mt-1">{resendForm.formState.errors.email.message}</p>
                  )}
                </div>
              )}
            />
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={resendSubmitting}
              >
                {resendSubmitting ? 'Envoi…' : 'Renvoyer le lien'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setState('invalid')}
                disabled={resendSubmitting}
              >
                Annuler
              </Button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  if (state === 'invalid-resend-sent') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-full max-w-md space-y-6 p-8 bg-white rounded-xl shadow text-center">
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">Vérifiez votre boîte de réception</h2>
            <p className="text-sm text-muted-foreground">
              Si cet email correspond à une invitation en attente, un nouveau lien vient d'être envoyé. Vérifiez votre boîte de réception (et les spams).
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => { window.history.replaceState(null, '', '/'); window.location.reload() }}
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md space-y-6 p-8 bg-white rounded-xl shadow">
        <div>
          <h2 className="text-2xl font-bold">Créer votre compte</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Rejoignez <strong>{inviteData?.orgName}</strong> sur SustainTrack
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={inviteData?.email || ''}
              readOnly
              className="bg-gray-50 cursor-not-allowed"
            />
          </div>

          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <div>
                <Label htmlFor="name">Prénom / Nom (optionnel)</Label>
                <Input id="name" placeholder="Marie Dupont" {...field} />
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <div>
                <Label htmlFor="password">Mot de passe</Label>
                <Input id="password" type="password" placeholder="Minimum 8 caractères" {...field} />
                {errors.password && <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>}
              </div>
            )}
          />

          <Controller
            name="confirmPassword"
            control={control}
            render={({ field }) => (
              <div>
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input id="confirmPassword" type="password" placeholder="Répéter le mot de passe" {...field} />
                {errors.confirmPassword && <p className="text-sm text-red-600 mt-1">{errors.confirmPassword.message}</p>}
              </div>
            )}
          />

          <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={submitting}>
            {submitting ? 'Création en cours...' : 'Créer mon compte'}
          </Button>
        </form>
      </div>
    </div>
  )
}
